import React, { forwardRef, useEffect, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CalendarIcon,
  InvoiceIcon,
  MoneyCashIcon,
  TrashIcon,
} from '../../assets';
import { usePermissions } from '../../hooks/usePermissions';
import { DatePickerInput } from '../inputs/DatePicker';
import { GenericDropdown } from '../inputs/GenericDropdown';
import { MoneyInput } from '../inputs/MoneyInput';
import { TextInput } from '../inputs/TextInput';
import { Button } from './Button';

type Props = {
  loading?: boolean;
  control?: any;
  currentCurrency?: string;
  handleSubmit?: any;
  errors?: any;
  onCancel: () => void;
  submitted?: boolean;
};

type ItemsArrayProps = {
  control: any;
  errors: any;
  currentCurrency?: string;
};

const ItemsArray = ({ control, errors, currentCurrency }: ItemsArrayProps) => {
  const { t } = useTranslation('common');
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const taxOptions = [
    { value: 0, label: '0%' },
    { value: 4, label: '4%' },
    { value: 21, label: '21%' },
  ];

  const addItem = () => {
    append({
      concept: '',
      description: '',
      amount: 0,
      tax: 0,
    });
  };

  return (
    <div className="w-full">
      {fields.map((field, index) => (
        <div key={field.id} className="">
          <div className="flex flex-row gap-x-2">
            <div className="flex flex-row gap-4 w-full">
              {/* concept */}
              <div className="flex-1">
                <Controller
                  name={`items.${index}.concept`}
                  control={control}
                  rules={{
                    required: t('global.requiredField'),
                  }}
                  render={({ field: conceptField }) => (
                    <TextInput
                      extraClassname="!h-[37px]"
                      type="text"
                      styleType="blue"
                      selectedValue={conceptField.value}
                      onSelectChange={(value) => conceptField.onChange(value)}
                      label={t('singleCampaign.concept')}
                      placeholder={t('singleCampaign.concept')}
                      errors={errors.items?.[index]?.concept}
                    />
                  )}
                />
              </div>

              {/* concept */}
              <div className="flex-1">
                <Controller
                  name={`items.${index}.description`}
                  control={control}
                  rules={{
                    required: t('global.requiredField'),
                  }}
                  render={({ field: descriptionField }) => (
                    <TextInput
                      extraClassname="!h-[37px]"
                      type="text"
                      styleType="blue"
                      selectedValue={descriptionField.value}
                      onSelectChange={(value) =>
                        descriptionField.onChange(value)
                      }
                      label={t('singleCampaign.invoiceDescription')}
                      placeholder={t('singleCampaign.invoiceDescription')}
                      errors={errors.items?.[index]?.description}
                    />
                  )}
                />
              </div>

              {/* Amount */}
              <div className="flex-1">
                <MoneyInput
                  height="h-[37px]"
                  control={control}
                  type="blue"
                  name={`items.${index}.amount`}
                  label={t('singleCampaign.amount')}
                  validation={{
                    required: t('global.requiredField'),
                  }}
                  errors={errors.items?.[index]?.amount}
                  currentCurrency={currentCurrency}
                  fullWidth
                />
              </div>

              {/* Tax Percentage */}
              <div className="flex-1">
                <GenericDropdown
                  height="h-[37px]"
                  control={control}
                  name={`items.${index}.tax`}
                  type="blue"
                  label={t('singleCampaign.tax')}
                  options={taxOptions}
                  placeholder={t('singleCampaign.tax')}
                  validation={{
                    required: t('global.requiredField'),
                  }}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => remove(index)}
              className="text-red-500 hover:text-red-700 text-sm mt-6"
            >
              <TrashIcon className="size-5" />
            </button>
          </div>
        </div>
      ))}

      {fields.length === 0 ? (
        <div className="mt-2 text-start text-gray-500 flex flex-row items-center h-full">
          <p>{t('singleCampaign.no-items')}</p>
          <Button
            style="light-blue-underline"
            text={t('singleCampaign.add-items')}
            onClick={addItem}
            customClassnames="hover:text-influentia-light-blue"
          />
        </div>
      ) : (
        <div className="flex items-center justify-center w-full">
          <Button
            style="light-blue-underline"
            text={t('singleCampaign.add-items')}
            onClick={addItem}
            customClassnames="hover:text-influentia-light-blue"
          />
        </div>
      )}
    </div>
  );
};

const BrandInvoiceForm = forwardRef(
  (
    {
      handleSubmit,
      loading,
      control,
      currentCurrency,
      errors,
      onCancel,
      submitted,
    }: Props,
    ref,
  ) => {
    const [showForm, setShowForm] = useState<boolean>(false);
    const { t } = useTranslation('common');

    const { isInfluencer, isInfluentia } = usePermissions();

    useEffect(() => {
      if (submitted) {
        setShowForm(false);
      }
    }, [submitted]);
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (handleSubmit) {
            handleSubmit();
          }
        }}
        className="flex flex-col"
      >
        {showForm ? (
          <div className="w-full object-contain flex items-center justify-center flex-col text-sm font-inter text-light-blue">
            <div className="flex flex-row w-full items-center gap-x-2">
              {/* <div
                  className={`flex flex-col gap-2.5 min-w-[200px] items-center`}
                >
                  <div className="flex flex-col gap-2.5 text-center items-center">
                    <div>
                      <FileIcon className="size-[35px]" />
                    </div>
                    <span>{t('singleCampaign.selectedFile')}</span>
                    <span>{file.name}</span>
                  </div>
                </div> */}
              <div className="flex flex-row gap-x-5 w-full">
                <div
                  className={`flex flex-row gap-1.5 w-full ${isInfluencer() && 'hidden'}`}
                >
                  <div className="bg-white rounded-full size-7 p-1.5 flex mt-4 items-center justify-center">
                    <CalendarIcon className="size-[16px] text-influentia-black" />
                  </div>
                  {isInfluentia() && control && (
                    <DatePickerInput
                      height="h-[37px]"
                      control={control}
                      type="blue"
                      name="billingDate"
                      label={t('global.billingDate')}
                      errors={errors.billingDate}
                      validation={{
                        required: t('singleCampaign.billingDateRequired'),
                      }}
                      fullSpace
                    />
                  )}
                </div>
                <div className="flex flex-row gap-1.5 w-full">
                  {isInfluentia() && control && (
                    <>
                      <div className="bg-white rounded-full size-7 p-1.5 flex mt-4 items-center justify-center">
                        <CalendarIcon className="size-[16px] text-influentia-black" />
                      </div>
                      <DatePickerInput
                        height="h-[37px]"
                        control={control}
                        type="blue"
                        name="paymentDate"
                        label={t('global.paymentDate')}
                        fullSpace
                      />
                    </>
                  )}
                </div>
                {isInfluentia() && control && (
                  <div className="flex flex-row gap-1.5 w-full">
                    <div className="bg-white rounded-full size-7 p-1.5 flex mt-4 items-center justify-center">
                      <CalendarIcon className="size-[16px] text-influentia-black" />
                    </div>
                    <GenericDropdown
                      control={control}
                      name="country"
                      options={[
                        { value: 'spain', label: 'Spain' },
                        { value: 'andorra', label: 'Andorra' },
                      ]}
                      label={t('global.billingCountry')}
                      labelClassname="!my-1 font-medium !text-[10px] text-influentia-light-blue block"
                      validation={{
                        required: t('global.requiredField'),
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Array de items */}
            <div className="flex flex-row gap-1.5 w-full">
              <div className="bg-white rounded-full size-7 p-1.5 flex mt-4 items-center justify-center">
                <MoneyCashIcon className="size-[16px] text-influentia-black" />
              </div>
              <ItemsArray
                currentCurrency={currentCurrency}
                control={control}
                errors={errors}
              />
            </div>
            <div
              className={`flex flex-row gap-1.5 w-full ${isInfluencer() && 'hidden'}`}
            >
              <div className="bg-white rounded-full size-7 p-1.5 mt-4 flex items-center justify-center">
                <MoneyCashIcon className="size-[16px] text-influentia-black" />
              </div>
              {isInfluentia() && control && (
                <MoneyInput
                  height="h-[37px]"
                  control={control}
                  type="blue"
                  name="Price"
                  label={t('singleCampaign.invoiceCampaignPrice')}
                  validation={{
                    required: t('singleCampaign.PriceRequired'),
                  }}
                  currencySelect={true}
                  currentCurrency={currentCurrency}
                  errors={errors.Price}
                  fullWidth
                  disabled
                />
              )}
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowForm(true)}
            className="flex flex-col items-center justify-center size-full text-white bg-dark-blue w-max py-8 px-4 rounded-xl gap-y-2 hover:bg-influentia-light-blue"
          >
            <InvoiceIcon className="size-8" />
            <p>{t('singleCampaign.create-invoice')}</p>
          </button>
        )}
        <div
          className={`w-full flex flex-row gap-5 items-center justify-center mr-6 ${!showForm && 'hidden'}`}
        >
          <Button
            style="light-blue"
            text={t('global.post')}
            id="BrandInvoiceFormButton"
            loading={loading}
            type="submit"
            customClassnames="flex w-[25%] items-center justify-center mt-2"
          />
          <Button
            style="dark-blue"
            text={t('global.cancel')}
            loading={loading}
            customClassnames={`flex w-[25%] items-center !font-normal justify-center mt-2 ${showForm === null && 'hidden'}`}
            onClick={() => {
              setShowForm(false);
              onCancel();
            }}
          />
        </div>
      </form>
    );
  },
);

export default BrandInvoiceForm;
